import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { v4 as uuid } from "uuid";
import { get } from "../../services/teams";
/* import { getQuarters } from "../../services/teams"; Post-wave63 */
import { Panel } from "@workboard/wobo-ui";
import Table from "./components/Table";
import T from "./components/Toolbar";
import { useTranslation } from "react-i18next";

const titleId = uuid();
const DEFAULT_SORTED_COLUMN = "level";
const ROWSPERPAGE_VALUES = { pageNumbers: [10, 15, 25, 50, 100], isAll: false };
const RESET_ROWSPERPAGE_FACTOR = 0.8;

const Spacer = styled("div")``;

const TableBreakdown = ({ className, id, name, isLoading: isContainerLoading, ...props }) => {
  const mountedRef = useRef(true);
  const [isLoading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [pageData, setPageData] = useState({ columnName: DEFAULT_SORTED_COLUMN, order: "asc", page: 0, rowsPerPage: 10 });
  const [page, setPage] = useState(0);
  /* const [options, setOptions] = useState([]); Post-wave63 */
  const [filter, setFilter] = useState();
  const [checkRowsPerPage, setCheckRowsPerPage] = useState(false);
  const [key, setKey] = useState(uuid());
  const { t } = useTranslation()
  const handleFilter = useCallback(
    f => {
      setFilter(f);
    },
    [setFilter]
  );

  const handleTable = useCallback(
    f => {
      if (f.columnName === undefined) {
        setPageData({...f, columnName: DEFAULT_SORTED_COLUMN });

        // This table is always sorted (by Level as default), so reload to insure it doesn't ever
        // appear without a sort indicator. Else it will display incorrect state after 2 contiguous
        // sorting clicks to Level column (or after 3 clicks if starting w/diff column as sorted)
        setKey(uuid());
      } else {
        setPageData(f);
      }
    },
    [setPageData]
  );

  // Reset to first page when user (id) changed
  useEffect(() => {
    if (id !== undefined) {
      setPage(0);
      setPageData(pageData => ({ ...pageData, page: 0 }));
      setCheckRowsPerPage(true);
      setKey(uuid()); // Reload table.
    }
  }, [id, setPage, setPageData]);

  /* Post-wave63
  // Get filters when user (id) changed
  useEffect(() => {
    async function fetchFilters() {
      const response = await getQuarters({ id });
      if (!mountedRef.current) return null;
      setOptions(response);
    }

    if (id !== undefined) fetchFilters();
  }, [id, setOptions]);
  */

  useEffect(() => {
    async function fetchTeams() {
      setLoading(true);
      const response = await get(id, filter, { ...pageData });
      if (!mountedRef.current) return null;
      setTeams(response);

      if (checkRowsPerPage) {
        // If new total row count notably < current rows-per-page, reset to better fitting value
        if (response && response.totalTeamCount < pageData.rowsPerPage * RESET_ROWSPERPAGE_FACTOR) {
          const rpp = ROWSPERPAGE_VALUES.pageNumbers.find(val => val >= response.totalTeamCount)
            || ROWSPERPAGE_VALUES.pageNumbers[ROWSPERPAGE_VALUES.pageNumbers.length - 1];
            setPageData(pageData => ({ ...pageData, rowsPerPage: rpp }));
        }
        setCheckRowsPerPage(false);
      }

      setLoading(false);
    }

    if (id !== undefined) fetchTeams();
  }, [filter, page, pageData, setLoading, setTeams, setPageData]);

  const Toolbar = useCallback(
    () => <T id={id} name={name} onChange={handleFilter} teams={teams} titleId={titleId} />,
    [id, handleFilter, teams]
  );

  return (
    <>
    <Panel
      className={className}
      id={titleId}
      title={t("Team Breakdown")}
      toolbar={Toolbar}
      {...props}
    >
      <Table
        data={teams}
        pageData={pageData}
        rowsPerPageValues={ROWSPERPAGE_VALUES}
        isLoading={isLoading && isContainerLoading}
        onChange={handleTable}
        key={key}
      />
    </Panel>
    <Spacer style={{ height: '75px' }}></Spacer>
    </>
  );
};

export default styled(TableBreakdown)`
  padding: 0;
  grid-gap: 0;

  section {
    margin: 4px 20px 14px;
  }

  > header {
    padding: 8px 20px !important;
  }

  > div {
    padding: 0 !important;
  }

  ${Table} {
    table {
      margin: 0 !important;
    }

    // Table column sort icon hover
    .MuiTableSortLabel-icon {
      fill: #2099C5; // TODO: blue-400 when color palette updated to A11Y version.
    }

    // Table column sort icon default
    .MuiTableSortLabel-active > .MuiTableSortLabel-icon {
      fill: #687587; // TODO: gray-500 when color palette updated to A11Y version.
    }

    thead {
      margin: 0;
    }
  
    thead,
    thead > tr,
    thead > tr > th,
    thead > tr > th > span {
      height: 100% !important;
      min-height: 63px !important;
    }

    th {
      background: transparent;
      max-width: 80px;
    }
    th:nth-of-type(1),
    th:nth-of-type(2) {
      min-width: 180px;
    }
    th:nth-of-type(11) {
      min-width: 160px;
    }
  }
`;
